import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { EmailLanguageSchema } from './email.js';
import { PermissionSchema } from './roles.js';
const c = initContract();
/**
 * The Users screen's API (PART 15.2, ADM-05) plus the ownership transfer of
 * §15.2.10b and the invite-acceptance half of the flow.
 *
 * The R18 shape, end to end:
 *  - an invitation carries NO role (BR-ADM-05-16/BR-PERM-73) — the person
 *    arrives on the rung-4 floor and is given a role later, from their row;
 *  - the Owner is a flag beside the name, never a Role-column value, and
 *    every destructive action is refused on that row (BR-PERM-67/77);
 *  - suspension signs the person out AT ONCE (BR-ADM-05-07) and never
 *    touches what they created;
 *  - ownership moves only through the explicit transfer: Owner-initiated,
 *    Active target, typed confirmation, atomic, two audit rows
 *    (BR-ADM-05-15/BR-PERM-65/68).
 *
 * INVITATION EMAILS ARE SENT. `invite` and `resendInvite` queue a
 * `user_invitation` message in the SAME transaction as the invited row, so a
 * rolled-back invitation cannot leave a mail behind and a committed one cannot
 * fail to queue one (the transactional-outbox property DECISION.md §2.11 buys
 * the whole job runner for).
 *
 * The responses still carry `activationPath`. That is not a leftover: with the
 * `mock` provider — development, tests, and any deployment before the owner
 * creates the Resend account (TODO(deploy)) — nothing leaves the building, and
 * the copyable link is what keeps the flow usable. The UI labels it for
 * exactly that.
 */
export const USER_MESSAGES = {
    /** ERR-ADM-05-01 / ERR-ADM-05-07 / BR-ADM-05-01 — one sentence, quoted twice. */
    emailInUse: 'Somebody with that email is already invited. Check the list.',
    /**
     * BR-ADM-05-12 — Active plus Invited must not go past the seats the plan
     * allows, "whether or not they hold a role". The plan name is title-cased
     * from the tenant's stored plan so the sentence reads as the rule writes
     * it ("the Business plan").
     *
     * THE SPEC QUOTES TWO WORDINGS OF THIS ONE REFUSAL and this is the longer:
     * ERR-ADM-07-03 ends "Suspend somebody, or ask the account owner to switch
     * plan", while ERR-ADM-05-06 and ERR-ADM-05-09 end "Suspend somebody or
     * switch plan". The longer one is the only one that is TRUE for the person
     * most likely to read it: switching plan is BR-PERM-69's owner-only act, so
     * telling an administrator to switch plan sends them at a door they cannot
     * open. One sentence, and it is the accurate one.
     */
    seatsFull: (seats, plan) => `You have used all ${seats} seats on the ${plan} plan. Suspend somebody, or ask the account owner to switch plan.`,
    /** ERR-ADM-05-03 / BR-ADM-05-04. */
    ownAccount: 'You cannot switch off your own account.',
    /** ERR-ADM-05-04 / BR-ADM-05-05. */
    ownerAccount: "This is the owner's account. Transfer ownership before making this change.",
    /** ERR-ADM-05-12. */
    transferTargetNotActive: 'Ownership can only go to somebody who can already sign in. Ask them to accept their invitation first.',
    /** §10b step 4 — the typed confirmation must equal the receiver's name. */
    transferConfirmMismatch: 'Type the new owner’s full name exactly to confirm the transfer.',
    /** BR-PERM-72 — an admin's row answers to the Owner alone. */
    adminUntouchable: 'Only the owner can change an administrator’s account.',
    /** BR-PERM-27 + BR-ADM-05-10 — shown on the activation link itself. */
    inviteInvalid: 'This invitation is no longer valid. Ask your administrator.',
    /** Reactivation applies to suspended accounts; revoke/resend to invited ones. */
    wrongStatus: 'That action does not apply to this account in its current state.',
    notFound: 'You do not have permission to open this page. Ask your administrator.',
};
/**
 * D46 — ADM-05's seat-limit refusal, structured. `seats` is a genuine count
 * but the noun "seats" is fixed plural (the sentence never reads "1 seats"), so
 * it interpolates raw; `plan` is a display label. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const USER_REFUSALS = {
    seatsFull: (seats, plan) => ({
        message: USER_MESSAGES.seatsFull(seats, plan),
        key: 'users.errSeatsFull',
        params: { seats, plan },
    }),
};
const MessageSchema = z.object({ message: z.string() }).strict();
export const OrgUserSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    /** Always rendered left-to-right, even in Arabic (§15.2.4). */
    email: z.string(),
    /** R18 — one role or none; none renders as "— no role", a state not a gap. */
    roleId: z.uuid().nullable(),
    roleName: z.string().nullable(),
    status: z.enum(['invited', 'active', 'suspended', 'deactivated']),
    /** Exactly one row per company carries true (BR-PERM-65). */
    isOwner: z.boolean(),
    /** F-66 — worked out from the Audit Log; null for an Invited person. */
    lastActiveAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * BR-PERM-29 — "The system must be able to list, on demand, every account
     * whose person is a terminated employee, and every account with no
     * employee record at all. Both are legitimate — an outside accountant
     * needs a login and no salary — but both must be visible to somebody, or
     * the hole reopens quietly."
     *
     * Three values, and they state FACTS about the employee↔account link
     * (`employees.user_id`), deliberately nothing more:
     *  - `linked`     — an employee record points at this account and is not
     *                   terminated (active, on leave, or suspended);
     *  - `terminated` — the employee record pointing here is terminated;
     *  - `none`       — no employee record points at this account.
     *
     * NOT a statement about whether the account can sign in. Whether a
     * terminated person's account is actually switched off is exactly the
     * open hole of owner decision 24 (§16.6A, MD-10) — this field is the
     * visibility net that makes such an account FINDABLE, and it must not
     * imply the answer the owner has not yet given.
     */
    employment: z.enum(['linked', 'terminated', 'none']),
    /**
     * ERR-ADM-05-11's surviving half. The transactional enqueue makes the
     * box-level "partial success" unreachable (a rolled-back invite leaves no
     * mail; a committed one cannot have failed to queue one — see
     * users.service.ts §6a), but the QUEUED send can still fail PERMANENTLY
     * in the dispatcher. When it does, the person sits Invited with a link
     * that never arrived, and this flag is how the Users screen can finally
     * say the row's sentence: "The person was added but we could not send the
     * email. Use Resend invitation on their row." True only for an Invited
     * person whose LATEST invitation email reached the outbox's terminal
     * `failed` state; resending queues a fresh message and clears it.
     */
    inviteUndeliverable: z.boolean(),
})
    .strict();
export const InviteUserRequestSchema = z
    .object({
    email: z
        .email()
        .max(320)
        .transform((value) => value.toLowerCase()),
    fullName: z.string().trim().min(1).max(80),
    /** The optional line added to the invitation email (§6a #5). */
    message: z.string().trim().max(300).optional(),
    /**
     * The language to write to this person in. Optional, because an inviter
     * usually does not know: when it is absent the invitation is written in
     * the COMPANY's language (its Owner's), never in the inviter's current UI
     * locale — a bilingual administrator switching to English to read the docs
     * must not thereby send an Arabic colleague an English invitation.
     */
    language: EmailLanguageSchema.optional(),
})
    .strict();
export const InviteUserResponseSchema = z
    .object({
    user: OrgUserSchema,
    /**
     * The same `/accept-invite?token=…` link the queued email carries. Kept in
     * the response because under the `mock` provider no mail leaves the
     * building, and a copyable link is then the only way in.
     */
    activationPath: z.string(),
    expiresAt: z.string(),
})
    .strict();
export const TransferOwnershipRequestSchema = z
    .object({
    /** §10b step 4 — the current owner types the RECEIVER's full name. */
    confirmName: z.string().trim().min(1).max(80),
})
    .strict();
/** Accepting an invitation: the activation link's token + the new password. */
export const AcceptInviteRequestSchema = z
    .object({
    token: z.string().min(1).max(200),
    password: z
        .string()
        .transform((value) => value.slice(0, 200))
        .pipe(z
        .string()
        .min(10, 'Password must be at least 10 characters.')
        .regex(/[\p{L}]/u, 'Password must contain at least one letter.')
        .regex(/[0-9]/, 'Password must contain at least one number.')),
})
    .strict();
/**
 * G-08 "View as" — what the browser needs to draw the product as another
 * person sees it: their name for the banner, and the permission list `/api/me`
 * would answer THEM with (same shape as `MeResponse.permissions`: the ACTIVE
 * role's grant rows, sorted; empty for a role-less account).
 *
 * This is a READ, and the preview is client-side only. The caller's own
 * session is never changed — no cookie, no header, no server state names the
 * previewed person — so every request the browser makes while previewing is
 * still the Owner's, answered by the Owner's permissions. The web client
 * refuses to send writes while the preview is on (apps/web/src/api.ts), and the
 * server would have accepted them as the Owner's if it had not; that is why
 * the control is Owner-only and audit-logged (`user.view_as`).
 */
export const EffectivePermissionsResponseSchema = z
    .object({
    userId: z.uuid(),
    name: z.string(),
    permissions: PermissionSchema.array(),
})
    .strict();
const userParams = z.object({ userId: z.uuid() }).strict();
export const usersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/users',
        responses: {
            200: z.object({ users: OrgUserSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Everyone who can sign in (ADM-05): name, email, role-or-none, status, owner badge, last active.',
    },
    effectivePermissions: {
        method: 'GET',
        path: '/api/users/:userId/effective-permissions',
        pathParams: userParams,
        responses: {
            200: EffectivePermissionsResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'G-08 "View as": the permission list this person would get from /api/me, plus their name. Owner-only; writes a user.view_as audit row. A read — the caller’s session is unchanged.',
    },
    invite: {
        method: 'POST',
        path: '/api/users/invite',
        body: InviteUserRequestSchema,
        responses: {
            201: InviteUserResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Invite by email — creates an Invited row carrying NO role (BR-ADM-05-16) and queues the invitation email in the same transaction. Returns the activation link too, for the mock provider.',
    },
    resendInvite: {
        method: 'POST',
        path: '/api/users/:userId/resend-invite',
        pathParams: userParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: InviteUserResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'A fresh activation link for an Invited person; the old link stops working, and a new invitation email is queued.',
    },
    revokeInvite: {
        method: 'POST',
        path: '/api/users/:userId/revoke-invite',
        pathParams: userParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: z.object({ userId: z.uuid() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Invited → removed (§15.2.7): the row disappears; nothing stays but the Audit Log.',
    },
    suspend: {
        method: 'POST',
        path: '/api/users/:userId/suspend',
        pathParams: userParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: OrgUserSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch an account off and sign it out at once (BR-ADM-05-07). Refused on yourself, on the Owner (BR-PERM-67), and on an admin unless you are the Owner (BR-PERM-72).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/users/:userId/reactivate',
        pathParams: userParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: OrgUserSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Suspended → Active, with whatever role they had (§15.2.7).',
    },
    transferOwnership: {
        method: 'POST',
        path: '/api/users/:userId/transfer-ownership',
        pathParams: userParams,
        body: TransferOwnershipRequestSchema,
        responses: {
            200: z.object({ newOwnerId: z.uuid(), previousOwnerId: z.uuid() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Move the Owner flag (§15.2.10b): Owner-initiated, Active target, typed name, one atomic step, an audit row against each account (BR-ADM-05-15).',
    },
    acceptInvite: {
        method: 'POST',
        path: '/api/auth/accept-invite',
        body: AcceptInviteRequestSchema,
        responses: {
            200: z.object({ email: z.string() }).strict(),
            400: MessageSchema,
            410: MessageSchema,
        },
        summary: 'PUBLIC: the activation link — sets the password, turns Invited into Active with NO role. Validity is checked when the link is OPENED (BR-PERM-27).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=users.js.map